import { useState } from "react";
import { Reference } from "../../../__generated__/types";
import { Button, Stack, Typography, Alert, Box, CircularProgress } from "@mui/material";
import { ReferenceFormComponent } from "./form";
import { HdgCheckboxGroup } from "../../../components/hdg-checkbox-group";
import { useDesignerApplicationContext } from "../../../hooks/designer-application-context";
import { StaticReferenceList } from "./static-reference-list";
import { validateProfessionalReferences } from "../validations";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";

export const ReferenceForm = ({
  contactReference,
  setContactReference,
  hideNewForm = false,
  isEditable = true,
}: {
  contactReference: boolean;
  setContactReference: (value: boolean) => void;
  hideNewForm?: boolean;
  isEditable?: boolean;
}) => {
  const ctx = useDesignerApplicationContext();
  const { updateApplicationData, saveApplicationDataImmediately } = ctx;
  const application = (ctx as any).application;
  const [currentForm, setCurrentForm] = useState<Reference | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const value = (application?.references ?? []) as Reference[];

  // ---- VALIDATION ----
  const validation = validateProfessionalReferences(value);
  const hasRequiredRefs = validation.isValid;

  // ---------------------------------------

  const onChange = async (newValue: Reference[]) => {
    try {
      setIsSaving(true);
      await saveApplicationDataImmediately({ references: newValue });
      setCurrentForm(null);
    } catch (error) {
      // Fallback to regular update if immediate save fails
      updateApplicationData({ references: newValue });
      setCurrentForm(null);
    } finally {
      setIsSaving(false);
    }
  };

  const referenceFormStateProps = { value, onChange };

  if (!isEditable) {
    return <StaticReferenceList references={value} />;
  }

  return (
    <Stack direction="column" spacing={2}>
      {/* Saving indicator */}
      {isSaving && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={16} />
            <Typography variant="body2">Saving references...</Typography>
          </Stack>
        </Alert>
      )}

      {/* Validation Info */}
      {!validation.isValid && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Reference Requirements:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {validation.errors.map((error, index) => (
              <li key={index}>
                <Typography variant="body2" style={{ color: '#D97706' }}>{error}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {value.map((reference) => (
        <div className="work-experience-item" key={reference.id ?? `${reference.email}-${reference.firstName}`}>
          {currentForm?.id === reference.id ? (
            <ReferenceFormComponent
              key={reference.id ?? currentForm?.id}
              {...referenceFormStateProps}
              setCurrentForm={setCurrentForm}
              currentForm={currentForm}
            />
          ) : (
            <Stack direction="row" gap={2} justifyContent="space-between">
              <div>
                <h4 className="work-experience-title font-body font-size-4">
                  {reference.firstName} {reference.lastName}
                </h4>
                <span className="font-size-3">{reference.email}</span>
              </div>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  variant="text"
                  onClick={() => setCurrentForm(reference)}
                  sx={{
                    minWidth: 44,
                    height: 44,
                    padding: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    const newList = value.filter((_, i) => i !== value.indexOf(reference));
                    onChange(newList);
                  }}
                  sx={{
                    minWidth: 44,
                    height: 44,
                    padding: 0,
                    color: 'rgba(0, 0, 0, 0.6)',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: 'rgba(0, 0, 0, 0.8)',
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </Button>
              </Stack>
            </Stack>
          )}
        </div>
      ))}

      {/* Rule copy + live counts - only show when criteria not met */}
      {!hasRequiredRefs && (
        <Typography variant="body2" style={{ color: '#D97706' }}>
          You must provide <b>4 references</b> total: <b>2 Client</b> and{" "}
          <b>2 Professional/Vendor</b>. Current — Total: {value.length}
        </Typography>
      )}

      {/* Add Reference Button */}
      {currentForm === null && !hideNewForm && (
        <Button
          variant="outlined"
          onClick={() => setCurrentForm({} as Reference)}
          sx={{ mb: 2 }}
        >
          Add Reference
        </Button>
      )}

      {/* Reference Form - only show when currentForm is set */}
      {currentForm && !hideNewForm && (
        <ReferenceFormComponent 
          {...referenceFormStateProps} 
          currentForm={currentForm}
          setCurrentForm={setCurrentForm}
        />
      )}

      {/* Consent checkbox is locked until reference requirement is met */}
      <div
        style={{
          pointerEvents: hasRequiredRefs ? "auto" : "none",
          opacity: hasRequiredRefs ? 1 : 0.5,
        }}
      >
        <HdgCheckboxGroup
          value={contactReference ? ["contact-reference"] : []}
          onChange={(v) => {
            if (!hasRequiredRefs) return; // extra guard
            setContactReference(!!v.length);
          }}
          options={[
            {
              value: "contact-reference",
              label: "By checking this box you give us permission to contact the references provided",
            },
          ]}
        />
      </div>

    </Stack>
  );
};
